import React from 'react';
import {Box, Container, Grid, Typography, Link, Divider, alpha, useTheme, Stack} from '@mui/material';
import {useTranslation} from "react-i18next";
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import FastfoodIcon from '@mui/icons-material/Fastfood';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

const Footer = () => {
  const {t} = useTranslation();
  const theme = useTheme();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const productLinks = [
    {label: t('Features'), href: '#features'},
    {label: t('Pricing'), href: '#pricing'},
    {label: t('FAQ'), href: '#faq'},
    {label: t('Demo'), href: 'https://qrseller-client.netlify.app/?restaurant_id=2', external: true},
  ];

  const companyLinks = [
    {label: t('About Us'), href: '#about'},
    {label: t('Contact'), href: '#contact'},
    {label: t('Blog'), href: '#blog'},
    {label: t('Careers'), href: '#careers'},
  ];

  const legalLinks = [
    {label: t('Privacy Policy'), href: '#privacy'},
    {label: t('Terms of Service'), href: '#terms'},
    {label: t('Cookie Policy'), href: '#cookies'},
  ];

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'background.paper',
        borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        pt: 8,
        pb: 4,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{mb: 6}}>
          {/* Brand Column */}
          <Grid item xs={12} md={4}>
            <Box 
              sx={{
                display: 'flex', 
                alignItems: 'center', 
                mb: 2,
                cursor: 'pointer',
                width: 'fit-content',
              }}
              onClick={scrollToTop}
            >
              <FastfoodIcon sx={{mr: 1, color: 'primary.main', fontSize: 32}} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: 'primary.main',
                }}
              >
                QRseller
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                lineHeight: 1.7,
                mb: 3,
                maxWidth: 300,
              }}
            >
              {t("Empowering small businesses to go digital. Launch your menu and ordering system in minutes.")}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
              }}
            >
              {t("Founded by Yair Roshal")}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {t("Product Lead & CEO")}
            </Typography>
          </Grid>

          {/* Product Links */}
          <Grid item xs={6} md={2}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 2,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
              }}
            >
              {t("Product")}
            </Typography>
            <Stack spacing={1.5}>
              {productLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'color 0.2s',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Company Links */}
          <Grid item xs={6} md={2}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 2,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
              }}
            >
              {t("Company")}
            </Typography>
            <Stack spacing={1.5}>
              {companyLinks.map((link) => (
                <Link
                  key={link.label}
                  href={link.href}
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    transition: 'color 0.2s',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Stack>
          </Grid>

          {/* Contact Column */}
          <Grid item xs={12} md={4}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 2,
                textTransform: 'uppercase',
                fontSize: '0.75rem',
                letterSpacing: '0.05em',
              }}
            >
              {t("Get in Touch")}
            </Typography>
            <Stack spacing={2}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <EmailIcon sx={{fontSize: 20, color: 'primary.main'}} />
                <Link
                  href="mailto:pixelmegabit@gmail.com"
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  pixelmegabit@gmail.com
                </Link>
              </Box>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <PhoneIcon sx={{fontSize: 20, color: 'primary.main'}} />
                <Link
                  href="tel:+972587705778"
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  +972 58-770-5778
                </Link>
              </Box>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <WhatsAppIcon sx={{fontSize: 20, color: '#25D366'}} />
                <Link
                  href="https://wa.me/972587705778"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: 'text.secondary',
                    textDecoration: 'none',
                    fontSize: '0.875rem',
                    '&:hover': {
                      color: '#25D366',
                    },
                  }}
                >
                  {t("Chat on WhatsApp")}
                </Link>
              </Box>
            </Stack>
          </Grid>
        </Grid>

        <Divider sx={{mb: 4}} />

        {/* Bottom Row */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: {xs: 'column', sm: 'row'},
            justifyContent: 'space-between',
            alignItems: {xs: 'center', sm: 'center'},
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
            }}
          >
            © {new Date().getFullYear()} QRseller Ltd. {t("All rights reserved.")}
          </Typography>

          {/* Legal Links */}
          <Box sx={{display: 'flex', gap: 3, flexWrap: 'wrap', justifyContent: 'center'}}>
            {legalLinks.map((link) => (
              <Link
                key={link.label}
                href={link.href}
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  transition: 'color 0.2s',
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                {link.label}
              </Link>
            ))}
          </Box>
        </Box>

        {/* Netlify Status Badge */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 3,
          }}
        >
          <Box
            component="a"
            href="https://app.netlify.com/projects/qrseller/deploys"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
            }}
          >
            <img
              src="https://api.netlify.com/api/v1/badges/1ce8bf83-ec8a-4884-af4a-3fe8e0a5f293/deploy-status"
              alt="Netlify Status"
              style={{height: '20px'}}
            />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer;